import { Link } from "react-router-dom";
import { ArrowLeft, ShieldAlert, CalendarCheck, Search, Flag, ClipboardCheck, Mail } from "lucide-react";
import { translations } from "../i18n/translations";
import { useLang } from "../i18n/useLang";

const LOGO =
  "https://customer-assets.emergentagent.com/job_focus-love-app/artifacts/wqa1c5lx_reveal%20logo.png";
const LEGAL_EMAIL = "legal@getrevealapp.com";

const Bullets = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-3 text-reveal-cream/75 leading-relaxed">
        <span className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-reveal-rose" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export default function CsaePolicy() {
  const lang = useLang();
  const t = (translations[lang] || translations.en).csae;
  const mailto = `mailto:${LEGAL_EMAIL}`;

  return (
    <div className="min-h-screen bg-reveal-black text-reveal-cream font-body px-5 py-16" data-testid="csae-policy-page">
      <div className="max-w-2xl mx-auto">
        <Link
          to="/landing"
          data-testid="csae-back-link"
          className="inline-flex items-center gap-2 text-sm text-reveal-rose hover:text-reveal-cream transition-colors mb-10"
        >
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </Link>

        <div className="flex flex-col items-center text-center mb-12">
          <img src={LOGO} alt="Reveal" className="w-24 h-24 object-contain mb-6" data-testid="csae-logo" />
          <h1 className="font-heading text-4xl sm:text-5xl text-reveal-cream mb-4">{t.title}</h1>
          <p className="text-reveal-cream/70 leading-relaxed max-w-xl">{t.subtitle}</p>
        </div>

        <div className="space-y-11">
          <section data-testid="csae-section-1">
            <h2 className="font-heading text-2xl text-reveal-rose mb-4 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" /> {t.s1.h}
            </h2>
            <p className="text-reveal-cream/75 leading-relaxed mb-4">{t.s1.intro}</p>
            <Bullets items={t.s1.items} />
            <p className="text-reveal-cream/75 leading-relaxed mt-4">{t.s1.outro}</p>
          </section>

          <section data-testid="csae-section-2">
            <h2 className="font-heading text-2xl text-reveal-rose mb-4 flex items-center gap-2">
              <CalendarCheck className="w-5 h-5" /> {t.s2.h}
            </h2>
            <Bullets items={t.s2.items} />
          </section>

          <section data-testid="csae-section-3">
            <h2 className="font-heading text-2xl text-reveal-rose mb-4 flex items-center gap-2">
              <Search className="w-5 h-5" /> {t.s3.h}
            </h2>
            <Bullets items={t.s3.items} />
          </section>

          <section data-testid="csae-section-4">
            <h2 className="font-heading text-2xl text-reveal-rose mb-4 flex items-center gap-2">
              <Flag className="w-5 h-5" /> {t.s4.h}
            </h2>
            <Bullets items={t.s4.items} />
          </section>

          <section data-testid="csae-section-5">
            <h2 className="font-heading text-2xl text-reveal-rose mb-4 flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5" /> {t.s5.h}
            </h2>
            <p className="text-reveal-cream/75 leading-relaxed mb-4">{t.s5.intro}</p>
            <Bullets items={t.s5.items} />
          </section>

          <section data-testid="csae-section-6">
            <h2 className="font-heading text-2xl text-reveal-rose mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5" /> {t.s6.h}
            </h2>
            <p className="text-reveal-cream/75 leading-relaxed mb-4">{t.s6.intro}</p>
            <div className="rounded-2xl border border-reveal-rose/20 bg-reveal-burgundy/40 p-5 text-reveal-cream/80">
              <p>
                <span className="text-reveal-rose">{t.s6.emailLabel}:</span>{" "}
                <a href={mailto} className="text-reveal-cream hover:text-reveal-rose transition-colors underline underline-offset-4" data-testid="csae-email-link">
                  {LEGAL_EMAIL}
                </a>
              </p>
            </div>
            <p className="text-sm text-reveal-cream/55 mt-4">{t.s6.respond}</p>
            <p className="text-sm text-reveal-cream/55 mt-2">{t.s6.emergency}</p>
          </section>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-reveal-cream/45">
          <span data-testid="csae-effective">{t.effective}</span>
          <span data-testid="csae-updated">{t.updated}</span>
        </div>

        <div className="mt-10 pt-8 border-t border-reveal-rose/10 text-center">
          <Link
            to="/landing"
            data-testid="csae-home-link"
            className="inline-flex items-center gap-2 text-sm text-reveal-rose hover:text-reveal-cream transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> {t.home}
          </Link>
        </div>
      </div>
    </div>
  );
}
